/**
 * @file contains helper functions for AttachmentSingle component
 */
import { File as CustomFile } from '../../../generated/graphql';
import { AttachmentType, ExtensionType } from '../../../models/attachment';

/** @todo Remove after forms.ts are updated on the BE side */
const FILE__EXTENSIONS = {
  ai: 'image',
  csv: 'csv',
  doc: 'other',
  docx: 'other',
  ico: 'image',
  jpeg: 'image',
  jpg: 'image',
  key: 'other',
  odp: 'other',
  ods: 'other',
  odt: 'other',
  pdf: 'pdf',
  png: 'image',
  pps: 'other',
  ppt: 'other',
  pptx: 'other',
  ps: 'image',
  psd: 'image',
  rtf: 'other',
  tex: 'other',
  txt: 'other',
  wpd: 'other',
  xls: 'csv',
  xlsx: 'csv',
};

export const iconColorMap: Record<string, string> = {
  csv: 'var(--color-file-icon-csv)',
  image: 'var(--color-file-icon-img)',
  other: 'var(--color-file-icon-other)',
  pdf: 'var(--color-file-icon-pdf)',
};

/**
 * Get icon color for attachment with extension
 *
 * @param fileExtension Extension of file
 * @returns             Icon color
 */
export const getFileIconColor = (
  fileExtension?: CustomFile['extension'],
): string => {
  const fileExtensionExists =
    fileExtension !== undefined && fileExtension in FILE__EXTENSIONS;

  if (fileExtensionExists) {
    return iconColorMap[FILE__EXTENSIONS[fileExtension as ExtensionType]];
  }

  return iconColorMap.other;
};

/**
 * Check if attachment is being uploaded
 *
 * @param attachment Current attachment
 * @returns          Whether the current attachment is uploading
 */
export const getIsUploading = (
  attachment: AttachmentType,
): boolean | undefined =>
  'isUploading' in attachment ? attachment.isUploading : undefined;

/**
 * Remove file from draft or call delete mutation.
 * Depends on attachment status (draft or existing)
 *
 * @param attachment        Current attachment
 * @param canDeleteFile     Whether the current user has permissions to delete a file
 * @param requestFileRemove Callback for file removal
 */
export const handleFileRemove = (
  attachment: AttachmentType,
  canDeleteFile: boolean,
  requestFileRemove?: (key: CustomFile['key']) => void,
) => {
  const isAttachmentDraft = 'isDraft' in attachment && requestFileRemove;
  const isAttachmentExisting =
    'id' in attachment && requestFileRemove && canDeleteFile === true;

  if (isAttachmentDraft) {
    requestFileRemove(attachment.key);
  }

  if (isAttachmentExisting) {
    requestFileRemove(attachment.id);
  }
};
